import { useCallback, useEffect, useRef, useState } from "react";
import { MessagesSquare, Send, Loader2, ShieldAlert, Flag, LogOut, EyeOff, Lightbulb, Gift as GiftIcon } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { usePageMeta } from "@/lib/seo";
import { useAuth } from "@/context/AuthContext";
import { spooky } from "@/components/halloween/spooky";

const Rule = () => (
  <div className="flex items-start gap-3 rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 text-amber-900" data-testid="secret-rule">
    <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5" />
    <p className="text-sm"><b>REGEL: Verrate niemals deinen Namen.</b> Auch keine Handynummer, E-Mail oder Social-Media-Namen – solche Nachrichten werden automatisch ausgeblendet. <b data-testid="secret-ban-warning">Verrätst du deinen Namen, wirst du für diese Funktion gesperrt.</b></p>
  </div>
);

const Intro = ({ status, onJoin, onLeave, busy }) => (
  <div className="secret-card rounded-[2rem] p-6 sm:p-8 text-white text-center space-y-4" data-testid="secret-intro">
    <span className="secret-orb mx-auto w-20 h-20 rounded-full flex items-center justify-center"><MessagesSquare className="w-10 h-10" /></span>
    <h1 className="text-3xl sm:text-4xl font-extrabold">Heimlicher Chat</h1>
    <p className="text-white/80 max-w-md mx-auto">Du bekommst einen geheimen Chatpartner aus der Community. Ihr seht nur eure Decknamen – wer dahintersteckt, kommt niemals raus.</p>
    {status === "banned" ? (
      <p className="font-bold rounded-2xl bg-white/15 px-4 py-3" data-testid="secret-banned">Du bist für den heimlichen Chat gesperrt, weil du deinen Namen verraten hast.</p>
    ) : status === "waiting" ? (
      <div className="space-y-2" data-testid="secret-waiting">
        <p className="font-bold flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Wir suchen deinen geheimen Chatpartner …</p>
        <p className="text-xs text-white/70">Sobald jemand bereit ist, bekommst du eine Push-Nachricht.</p>
        <button onClick={onLeave} data-testid="secret-leave" className="text-xs underline text-white/70">Doch nicht</button>
      </div>
    ) : (
      <button onClick={onJoin} disabled={busy} data-testid="secret-join" className="h-12 px-8 rounded-full bg-white text-slate-900 font-extrabold shadow-lg active:scale-95 transition-transform">Geheimen Chatpartner finden</button>
    )}
  </div>
);

const REACTIONS = ["❤️", "😂"];

const GiftBubble = ({ m, onThank }) => (
  <div className={`flex flex-col ${m.mine ? "items-end" : "items-start"} gap-1`}>
    <div className="rounded-2xl px-4 py-2.5 bg-gradient-to-br from-amber-300 to-pink-400 text-white font-extrabold text-sm shadow react-pop" data-testid={`secret-gift-msg-${m.id}`}>
      🎁 {m.mine ? `Du hast ${m.gift} Punkte geschenkt` : `Dir wurden ${m.gift} Punkte geschenkt`}
    </div>
    {m.revoked ? <span className="text-[11px] font-bold text-slate-500" data-testid={`secret-gift-revoked-${m.id}`}>Von der Moderation zurückgenommen</span>
      : m.thanked ? <span className="text-[11px] font-bold text-pink-600 bg-pink-50 border border-pink-200 rounded-full px-2 py-0.5 react-pop" data-testid={`secret-gift-thanked-${m.id}`}>{m.mine ? "Danke 💖 erhalten" : "Danke 💖 gesendet"}</span>
      : !m.mine && <button type="button" onClick={() => onThank(m.id)} data-testid={`secret-gift-thank-${m.id}`} className="h-8 px-3 rounded-full bg-white border border-pink-200 text-pink-600 text-xs font-extrabold hover:bg-pink-50 active:scale-95 transition-transform">Danke 💖</button>}
  </div>
);

const SEEN = "secret-gifts-seen";
const RAIN = Array.from({ length: 28 }, (_, i) => ({ x: (i * 37) % 100, d: (i % 7) * 0.18, s: i % 3 === 0 ? "💖" : i % 3 === 1 ? "🎉" : "✨" }));
const GiftRain = () => (
  <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden" data-testid="secret-gift-rain" aria-hidden>
    {RAIN.map((r, i) => <span key={i} className="absolute -top-8 text-2xl gift-rain" style={{ left: `${r.x}%`, animationDelay: `${r.d}s` }}>{r.s}</span>)}
  </div>
);
const useGiftRain = (msgs) => {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const seen = JSON.parse(localStorage.getItem(SEEN) || "[]");
    const fresh = msgs.filter((m) => m.gift && !m.mine && !seen.includes(m.id));
    if (!fresh.length) return undefined;
    localStorage.setItem(SEEN, JSON.stringify([...seen, ...fresh.map((m) => m.id)].slice(-100)));
    setOn(true);
    const t = setTimeout(() => setOn(false), 3800);
    return () => clearTimeout(t);
  }, [msgs]);
  return on;
};

const Bubble = ({ m, onReact, onThank }) => {
  const [open, setOpen] = useState(false);
  if (m.gift) return <GiftBubble m={m} onThank={onThank} />;
  const canReact = !m.mine && !m.hidden;
  const choose = (e) => { onReact(m.id, m.reaction === e ? null : e); setOpen(false); };
  return (
    <div className={`flex flex-col ${m.mine ? "items-end" : "items-start"} ${m.reaction ? "mb-2" : ""}`}>
      <div className="relative max-w-[80%]">
        <button type="button" disabled={!canReact} onClick={() => setOpen((v) => !v)} onDoubleClick={() => canReact && choose("❤️")}
          className={`text-left rounded-2xl px-3 py-2 text-sm break-words ${m.mine ? "bg-slate-900 text-white rounded-br-sm" : "bg-white border border-slate-200 rounded-bl-sm"} ${m.hidden ? "opacity-60" : ""} ${canReact ? "cursor-pointer" : "cursor-default"}`} data-testid={`secret-msg-${m.id}`}>
          {spooky(m.text, m.id)}
          {m.hidden && <span className="mt-1 text-[11px] flex items-center gap-1 text-amber-300" data-testid={`secret-msg-hidden-${m.id}`}><EyeOff className="w-3 h-3" />{m.hidden === "name" ? "Ausgeblendet: enthält Namen oder Kontaktdaten" : "Ausgeblendet: verstößt gegen die Regeln"}</span>}
        </button>
        {m.reaction && <span className={`absolute -bottom-2.5 ${m.mine ? "left-1" : "right-1"} text-sm bg-white rounded-full px-1 shadow border border-slate-100 react-pop`} data-testid={`secret-msg-reaction-${m.id}`}>{m.reaction}</span>}
      </div>
      {open && canReact && (
        <div className="mt-1.5 flex gap-1 rounded-full bg-white border border-slate-200 shadow p-1 react-pop" data-testid={`secret-react-bar-${m.id}`}>
          {REACTIONS.map((e) => <button key={e} type="button" onClick={() => choose(e)} data-testid={`secret-react-${m.id}-${e === "❤️" ? "heart" : "laugh"}`} className={`w-9 h-9 rounded-full text-lg hover:scale-110 transition-transform ${m.reaction === e ? "bg-rose-50" : ""}`}>{e}</button>)}
        </div>
      )}
      {m.mine && !m.hidden && <span className={`mt-1 text-[10px] font-semibold ${m.read ? "text-sky-500" : "text-slate-400"}`} data-testid={`secret-msg-status-${m.id}`}>{m.read ? "✓✓ Gelesen" : "✓ Gesendet"}</span>}
    </div>
  );
};

const ICEBREAKERS = [
  "Wenn du ein Tier wärst, welches und warum? 🐾", "Pizza mit Ananas – ja oder absolutes Nein? 🍍", "Was war das Peinlichste, das dir je passiert ist? 🙈",
  "Welche Superkraft hättest du am liebsten? ⚡", "Was ist dein Lieblings-Itzcouple-Video? 🎬", "Morgenmuffel oder Frühaufsteher? ☀️",
  "Wohin würdest du sofort reisen, wenn du könntest? ✈️", "Welches Lied singst du heimlich unter der Dusche? 🎤", "Was ist dein verstecktes Talent? ✨",
  "Süß oder salzig – was gewinnt? 🍫", "Was würdest du mit 1 Million Euro als Erstes machen? 💸", "Hund, Katze oder Hamster? 🐶",
  "Welche Serie könntest du immer wieder schauen? 📺", "Was bringt dich garantiert zum Lachen? 😂", "Wenn du einen Tag unsichtbar wärst, was würdest du tun? 👻",
];
const pick = () => [...ICEBREAKERS].sort(() => Math.random() - 0.5).slice(0, 3);

const Icebreakers = ({ onPick }) => {
  const [open, setOpen] = useState(false);
  const [qs, setQs] = useState(pick);
  return (
    <div className="relative">
      <button type="button" onClick={() => { setQs(pick()); setOpen((v) => !v); }} aria-label="Eisbrecher-Fragen" data-testid="secret-icebreaker" className="w-12 h-12 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center hover:bg-amber-200 transition-colors"><Lightbulb className="w-5 h-5" /></button>
      {open && (
        <div className="absolute bottom-14 left-0 z-20 w-72 max-w-[80vw] rounded-2xl bg-white border border-slate-200 shadow-xl p-2 space-y-1" data-testid="secret-icebreaker-menu">
          <p className="px-2 pt-1 text-[11px] font-extrabold tracking-wider text-slate-400">EISBRECHER</p>
          {qs.map((q, i) => <button key={q} type="button" onClick={() => { onPick(q); setOpen(false); }} data-testid={`secret-icebreaker-${i}`} className="w-full text-left text-sm px-3 py-2 rounded-xl hover:bg-amber-50">{q}</button>)}
          <button type="button" onClick={() => setQs(pick())} data-testid="secret-icebreaker-more" className="w-full text-xs font-bold text-amber-700 px-3 py-1.5 rounded-xl hover:bg-amber-50">Andere Fragen 🔄</button>
        </div>
      )}
    </div>
  );
};

const AMOUNTS = [5, 10, 20, 50];

const GiftPoints = ({ alias, onSent }) => {
  const { user, refreshUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [n, setN] = useState("");
  const [busy, setBusy] = useState(false);
  const pts = user?.points ?? 0;
  const send = async (amount) => {
    if (!amount || amount < 1) return;
    if (!window.confirm(`${amount} Punkte an ${alias} schenken? Sie werden von deinem Guthaben abgezogen.`)) return;
    setBusy(true);
    try { const { data } = await api.post("/secretchat/gift", { amount }); onSent(data); refreshUser(); setOpen(false); setN(""); toast.success(`${amount} Punkte an ${alias} geschenkt 🎁`); } catch (err) { toast.error(formatErr(err)); } finally { setBusy(false); }
  };
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-label="Punkte schenken" data-testid="secret-gift" className="w-12 h-12 rounded-full bg-pink-100 text-pink-600 flex items-center justify-center hover:bg-pink-200 transition-colors"><GiftIcon className="w-5 h-5" /></button>
      {open && (
        <div className="absolute bottom-14 left-0 z-20 w-72 max-w-[80vw] rounded-2xl bg-white border border-slate-200 shadow-xl p-3 space-y-2" data-testid="secret-gift-menu">
          <p className="text-[11px] font-extrabold tracking-wider text-slate-400" data-testid="secret-gift-balance">PUNKTE SCHENKEN · DU HAST {pts}</p>
          <div className="grid grid-cols-4 gap-1.5">
            {AMOUNTS.map((a) => <button key={a} type="button" disabled={busy || pts < a} onClick={() => send(a)} data-testid={`secret-gift-${a}`} className="h-10 rounded-xl bg-pink-50 text-pink-600 font-extrabold text-sm hover:bg-pink-100 disabled:opacity-40">{a}</button>)}
          </div>
          <div className="flex gap-1.5">
            <input type="text" inputMode="numeric" value={n} onChange={(e) => setN(e.target.value.replace(/\D/g, ""))} placeholder="Eigene Zahl" data-testid="secret-gift-custom" className="flex-1 min-w-0 h-10 px-3 rounded-xl border border-slate-200 text-sm" />
            <button type="button" disabled={busy || !Number(n)} onClick={() => send(Number(n))} data-testid="secret-gift-custom-send" className="h-10 px-3 rounded-xl bg-pink-500 text-white text-sm font-extrabold disabled:opacity-40">Schenken</button>
          </div>
          <p className="text-[11px] text-slate-400">Dein Partner sieht nur deinen Decknamen – nie deinen echten Namen.</p>
        </div>
      )}
    </div>
  );
};

const Typing = ({ alias }) => (
  <div className="flex items-center gap-2 text-xs text-slate-500 px-1" data-testid="secret-typing">
    <span className="inline-flex gap-1 rounded-2xl bg-white border border-slate-200 px-3 py-2">{[0, 1, 2].map((i) => <span key={i} className="w-1.5 h-1.5 rounded-full bg-slate-400 typing-dot" style={{ animationDelay: `${i * 150}ms` }} />)}</span>
    {alias} schreibt gerade …
  </div>
);

const Chat = ({ s, onEnd }) => {
  const [msgs, setMsgs] = useState([]);
  const [t, setT] = useState("");
  const [typing, setTyping] = useState(false);
  const box = useRef(null);
  const lastId = useRef(null);
  const rev = useRef(null);
  const sentAt = useRef(0);
  const load = useCallback(() => api.get("/secretchat/messages").then(({ data }) => { setMsgs(data); lastId.current = data[data.length - 1]?.id || null; }).catch(() => {}), []);
  useEffect(() => {
    load();
    const iv = setInterval(() => api.get("/secretchat/pulse").then(({ data }) => {
      setTyping(Boolean(data.typing));
      const changed = rev.current !== null && data.rev !== rev.current;
      rev.current = data.rev;
      if ((data.last_id && data.last_id !== lastId.current) || changed) load();
    }).catch(() => {}), 2500);
    return () => clearInterval(iv);
  }, [load]);
  const react = async (id, emoji) => {
    try { const { data } = await api.post(`/secretchat/messages/${id}/react`, { emoji }); setMsgs((ms) => ms.map((x) => (x.id === id ? { ...x, reaction: data.reaction } : x))); } catch (err) { toast.error(formatErr(err)); }
  };
  const thank = async (id) => {
    try { const { data } = await api.post(`/secretchat/messages/${id}/thank`); setMsgs((ms) => ms.map((x) => (x.id === id ? { ...x, thanked: data.thanked } : x))); } catch (err) { toast.error(formatErr(err)); }
  };
  const rain = useGiftRain(msgs);
  useEffect(() => { box.current?.scrollTo({ top: box.current.scrollHeight }); }, [msgs.length, typing]);
  const change = (v) => {
    setT(v);
    if (v && Date.now() - sentAt.current > 3000) { sentAt.current = Date.now(); api.post("/secretchat/typing").catch(() => {}); }
  };
  const send = async (e) => {
    e.preventDefault();
    if (!t.trim()) return;
    try {
      const { data } = await api.post("/secretchat/messages", { text: t });
      setMsgs((m) => [...m, data]); setT(""); lastId.current = data.id; sentAt.current = 0;
      if (data.hidden) toast.error("Nicht gesendet – keine Namen oder Kontaktdaten! 🤫");
    } catch (err) { toast.error(formatErr(err)); }
  };
  const report = async () => {
    const reason = window.prompt("Was ist passiert? (freiwillig)") ?? null;
    if (reason === null) return;
    try { await api.post("/secretchat/report", { reason }); toast.success("Danke – die Moderatoren schauen sich den Chat an"); } catch (err) { toast.error(formatErr(err)); }
  };
  const reportName = async () => {
    if (!window.confirm("Dein Partner hat seinen Namen verraten? Die letzten 20 Nachrichten gehen zur Prüfung an die Moderatoren.")) return;
    try { await api.post("/secretchat/report-name"); toast.success("Gemeldet – die Moderatoren prüfen den Chat"); } catch (err) { toast.error(formatErr(err)); }
  };
  const end = () => window.confirm("Heimlichen Chat wirklich beenden? Ihr werdet nie wieder verbunden.") && onEnd();
  return (
    <div className="space-y-3" data-testid="secret-chat">
      {rain && <GiftRain />}
      <div className="secret-card rounded-3xl p-4 text-white flex items-center gap-3">
        <span className="secret-orb w-12 h-12 rounded-full flex items-center justify-center text-xl font-extrabold shrink-0">?</span>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-extrabold tracking-[0.2em] text-white/70">DEIN GEHEIMER CHATPARTNER</p>
          <p className="text-lg font-extrabold truncate" data-testid="secret-partner-alias">{s.partner_alias}</p>
          <p className="text-xs text-white/70">Du bist „{s.my_alias}“</p>
        </div>
        <button onClick={report} aria-label="Melden" data-testid="secret-report" className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center"><Flag className="w-4 h-4" /></button>
        <button onClick={end} aria-label="Chat beenden" data-testid="secret-end" className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center"><LogOut className="w-4 h-4" /></button>
      </div>
      <Rule />
      <button onClick={reportName} data-testid="secret-report-name" className="w-full h-11 rounded-2xl bg-rose-50 border border-rose-200 text-rose-600 text-sm font-extrabold flex items-center justify-center gap-2 hover:bg-rose-100 transition-colors"><Flag className="w-4 h-4" /> Nutzer hat Name verraten – MELDEN</button>
      <div ref={box} className="h-[50vh] overflow-y-auto space-y-2 rounded-3xl bg-slate-50 border border-slate-200 p-3" data-testid="secret-messages">
        {msgs.length === 0 && <p className="text-xs text-slate-400 text-center py-8">Schreib die erste heimliche Nachricht 🤫 – oder tipp auf die Glühbirne für eine Eisbrecher-Frage</p>}
        {msgs.map((m) => <Bubble key={m.id} m={m} onReact={react} onThank={thank} />)}
        {typing && <Typing alias={s.partner_alias} />}
      </div>
      <form onSubmit={send} className="flex gap-2">
        <Icebreakers onPick={(q) => change(q)} />
        <GiftPoints alias={s.partner_alias} onSent={(m) => { setMsgs((x) => [...x, m]); lastId.current = m.id; }} />
        <input value={t} maxLength={500} onChange={(e) => change(e.target.value)} placeholder="Heimliche Nachricht …" data-testid="secret-input" className="flex-1 min-w-0 h-12 px-4 rounded-full border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300" />
        <button disabled={!t.trim()} aria-label="Senden" data-testid="secret-send" className="w-12 h-12 rounded-full bg-slate-900 text-white flex items-center justify-center disabled:opacity-40"><Send className="w-4 h-4" /></button>
      </form>
    </div>
  );
};

export default function SecretChat() {
  usePageMeta({ title: "Heimlicher Chat – itzPlay", description: "Chatte anonym mit einem geheimen Partner aus der Community." });
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/secretchat").then(({ data }) => setS(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (s?.status !== "waiting") return undefined; const iv = setInterval(load, 8000); return () => clearInterval(iv); }, [s?.status, load]);
  const act = async (path) => { setBusy(true); try { const { data } = await api.post(path); setS(data); } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); } };
  if (!s) return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  return (
    <div className="max-w-2xl mx-auto space-y-4" data-testid="secret-page">
      {s.status === "paired" ? <Chat s={s} onEnd={() => act("/secretchat/end")} /> : <><Intro status={s.status} busy={busy} onJoin={() => act("/secretchat/join")} onLeave={() => act("/secretchat/leave")} /><Rule /></>}
    </div>
  );
}
